import { useState } from "react";
import { Modal } from "../../components/ui/modal";
import { ApiV2Error } from "../../lib/api-v2";
import { cn } from "../../lib/utils";
import {
  PRODUCT_DESCRIPTION_MAX,
  PRODUCT_NAME_MAX,
  PRODUCT_PRICE_MAX,
  type Product,
  type ProductInput,
} from "./types";

interface ProductFormModalProps {
  /** The product being edited; omitted when creating. */
  product?: Product;
  saving: boolean;
  onSubmit: (input: ProductInput) => Promise<unknown>;
  onClose: () => void;
}

/** Same rules as the API: a rupee amount, never negative, at most two decimals. */
function priceError(raw: string): string | null {
  if (!raw.trim()) return "Enter a price.";
  if (!/^\d+(\.\d{1,2})?$/.test(raw.trim())) return "Use a number with up to 2 decimals, e.g. 6 or 4.50.";
  if (Number(raw) > PRODUCT_PRICE_MAX) return `Price can be at most ₹${PRODUCT_PRICE_MAX.toLocaleString("en-IN")}.`;
  return null;
}

/** Optional whole rupees, at least 1. */
function amountError(raw: string): string | null {
  if (!raw.trim()) return null;
  if (!/^\d+$/.test(raw.trim()) || Number(raw) < 1) return "Use whole rupees, e.g. 50000.";
  if (Number(raw) > PRODUCT_PRICE_MAX) return `Can be at most ₹${PRODUCT_PRICE_MAX.toLocaleString("en-IN")}.`;
  return null;
}

const optionalAmount = (raw: string) => (raw.trim() ? Number(raw) : null);

export function ProductFormModal({ product, saving, onSubmit, onClose }: ProductFormModalProps) {
  const [name, setName] = useState(product?.name ?? "");
  const [price, setPrice] = useState(product ? String(product.price) : "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [minAmount, setMinAmount] = useState(product?.min_amount != null ? String(product.min_amount) : "");
  const [maxAmount, setMaxAmount] = useState(product?.max_amount != null ? String(product.max_amount) : "");
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const nameError = name.trim() ? null : "Enter a product name.";
  const priceProblem = priceError(price);
  const minProblem = amountError(minAmount);
  const maxProblem =
    amountError(maxAmount) ??
    (!minProblem && minAmount.trim() && maxAmount.trim() && Number(maxAmount) < Number(minAmount)
      ? "Max amount must not be less than min amount."
      : null);
  const valid = !nameError && !priceProblem && !minProblem && !maxProblem;

  const submit = async () => {
    setTouched(true);
    if (!valid || saving) return;
    setServerError(null);
    try {
      await onSubmit({
        name: name.trim(),
        price: Number(price),
        description: description.trim() || null,
        min_amount: optionalAmount(minAmount),
        max_amount: optionalAmount(maxAmount),
      });
    } catch (err) {
      setServerError(err instanceof ApiV2Error ? err.message : "Could not save the product. Try again.");
    }
  };

  const field = (hasError: boolean) =>
    cn(
      "w-full rounded-lg border px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/30",
      hasError ? "border-danger/50" : "border-line focus:border-brand",
    );

  return (
    <Modal
      title={product ? "Edit Product" : "New Product"}
      subtitle={product ? product.name : "Add a product every account with Products turned on can see."}
      onClose={onClose}
      width="max-w-md"
      footer={
        <>
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
            Cancel
          </button>
          <button
            onClick={() => { void submit(); }}
            disabled={saving}
            className={cn(
              "rounded-lg px-4 py-2 text-sm font-semibold text-white",
              saving ? "cursor-wait bg-line text-ink-3" : "bg-brand hover:bg-brand-hover",
            )}
          >
            {saving ? "Saving…" : product ? "Save" : "Create"}
          </button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Name</span>
          <input
            autoFocus
            value={name}
            maxLength={PRODUCT_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Starter"
            className={field(touched && Boolean(nameError))}
          />
          {touched && nameError && <span className="mt-1 block text-xs text-danger">{nameError}</span>}
        </label>

        <label className="block">
          <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
            Price per minute (₹)
          </span>
          <input
            value={price}
            inputMode="decimal"
            onChange={(e) => setPrice(e.target.value)}
            placeholder="e.g., 6"
            className={field(touched && Boolean(priceProblem))}
          />
          {touched && priceProblem && <span className="mt-1 block text-xs text-danger">{priceProblem}</span>}
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
              Min amount (₹)
            </span>
            <input
              value={minAmount}
              inputMode="numeric"
              onChange={(e) => setMinAmount(e.target.value)}
              placeholder="e.g., 1000"
              className={field(touched && Boolean(minProblem))}
            />
            {touched && minProblem && <span className="mt-1 block text-xs text-danger">{minProblem}</span>}
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
              Max amount (₹)
            </span>
            <input
              value={maxAmount}
              inputMode="numeric"
              onChange={(e) => setMaxAmount(e.target.value)}
              placeholder="No limit"
              className={field(touched && Boolean(maxProblem))}
            />
            {touched && maxProblem && <span className="mt-1 block text-xs text-danger">{maxProblem}</span>}
          </label>
        </div>
        <p className="-mt-2 text-xs text-ink-3">
          Accounts buy this plan for any amount in the range (excl. GST) and get amount ÷ price per minute
          credits. Leave max empty for no upper limit.
        </p>

        <label className="block">
          <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
            Description (optional)
          </span>
          <textarea
            value={description}
            rows={4}
            maxLength={PRODUCT_DESCRIPTION_MAX}
            onChange={(e) => setDescription(e.target.value)}            className={cn(field(false), "resize-y")}
          />
          <span className="mt-1 block text-xs text-ink-3">Each line shows as a ✓ point on the Products page.</span>
        </label>

        {serverError && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">{serverError}</p>
        )}
        {/* Enter in a single-line field submits the form. */}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
